import { useEffect, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { useSelector } from "react-redux";
import { t } from "../../translations";
import { createLogger } from "../../utils/logger";
import { GET } from "../constants/request-type";
import { service_api } from "../constants/url";
import { MakeRequestAsync } from "../functions/axios";
import "./SubscriptionCountdownBanner.css";

const logger = createLogger("SubscriptionCountdownBanner");
const SubscriptionCountdownBanner = ({ onShowPaywall, lang = "en" }) => {
  const user = useSelector((state) => state.user);

  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (user?._id) {
      fetchSubscriptionStatus();
    }
  }, [user?._id]);

  const fetchSubscriptionStatus = async () => {
    try {
      setLoading(true);
      const response = await MakeRequestAsync({
        type: GET,
        url: service_api,
        route: `subscription/chef/${user._id}/status`,
        data: null,
        token: user?.refresh_token
      });

      setSubscriptionData(response.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="subscription-banner loading">
        <div className="banner-content">
          <div className="loading-spinner"></div>
          <span>{t("subscription.loading", lang)}</span>
        </div>
      </div>
    );
  }

  if (error || !subscriptionData) {
    return null; // Don't show banner if there's an error
  }

  const {
    fulfilledOrdersCount = 0,
    remainingOrders = 25,
    freeOrdersLimit = 25,
    subscriptionStatus = "none",
    subscriptionPrice = 24.99
  } = subscriptionData;

  // Don't show banner if chef has active subscription
  if (subscriptionStatus === "active") {
    return (
      <div className="subscription-banner active-subscription">
        <div className="banner-content">
          <FaCheckCircle className="success-icon" />
          <span>{t("subscription.active_subscription", lang)}</span>
        </div>
      </div>
    );
  }

  // Determine banner color and state
  let bannerClass = "subscription-banner";

  if (remainingOrders > 15) {
    bannerClass += " safe";
  } else if (remainingOrders > 5) {
    bannerClass += " warning";
  } else if (remainingOrders > 0) {
    bannerClass += " danger";
  } else {
    bannerClass += " blocked";
  }

  // Calculate progress percentage
  const progressPercentage = (fulfilledOrdersCount / freeOrdersLimit) * 100;

  return (
    <div className={bannerClass}>
      <div className="banner-content">
        <div className="banner-header">
          <span className="order-count">
            {fulfilledOrdersCount}/{freeOrdersLimit}{" "}
            {t("subscription.free_orders_used", lang)}
          </span>
          <div className="banner-message">
            {remainingOrders > 0 ? (
              <span>
                {t("subscription.only_orders_left", lang).replace(
                  "{count}",
                  remainingOrders
                )}
              </span>
            ) : (
              <span>{t("subscription.no_free_orders_left", lang)}</span>
            )}
          </div>
          <div className="progress-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.max(Math.min(progressPercentage, 100), 2)}%`,
                  minWidth: progressPercentage > 0 ? "2px" : "0px"
                }}
              ></div>
            </div>
          </div>
        </div>

        <div className="banner-action">
          {remainingOrders === 0 ? (
            <button
              className="subscribe-button urgent"
              onClick={() =>
                onShowPaywall ? onShowPaywall() : handleSubscribe()
              }
            >
              {t("subscription.subscribe_now", lang)} - ${subscriptionPrice}
              /month
            </button>
          ) : remainingOrders <= 5 ? (
            <button
              className="subscribe-button warning"
              onClick={() => handleSubscribe()}
            >
              {t("subscription.subscribe_to_keep_selling", lang)} - $
              {subscriptionPrice}/month
            </button>
          ) : (
            <span className="subscription-info">
              {t("subscription.subscribe_at_price", lang).replace(
                "{price}",
                `$${subscriptionPrice}/month`
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );

  async function handleSubscribe() {
    try {
      const response = await MakeRequestAsync({
        type: "POST",
        url: service_api,
        route: "subscription/checkout",
        data: { chefId: user._id },
        token: user?.refresh_token
      });

      if (response.data.url) {
        window.open(response.data.url, "_blank");
      }
    } catch (err) {
      // Handle subscription error silently
      // You might want to show an error message to the user
    }
  }
};

export default SubscriptionCountdownBanner;
